import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * ADR-0021 — typed wrappers for the super-admin Integrations callables.
 * Non-secret config + secret presence come back from `listIntegrationConfigs`;
 * `setIntegrationConfig` writes non-secret settings; `setIntegrationSecret`
 * pushes a secret value to Secret Manager (the value never round-trips back).
 */

const SettingValueSchema = z.union([z.string(), z.number(), z.boolean()]);

const SettingFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(['string', 'url', 'number', 'enum']),
  enumValues: z.array(z.string()).optional(),
  placeholder: z.string().optional(),
  required: z.boolean(),
  help: z.string().optional(),
});

const SecretStatusSchema = z.object({
  name: z.string(),
  label: z.string(),
  required: z.boolean(),
  isSet: z.boolean(),
  updatedAtMs: z.number().nullable(),
});

export const IntegrationItemSchema = z.object({
  service: z.string(),
  label: z.string(),
  summary: z.string(),
  status: z.enum(['live', 'planned']),
  launchRequirement: z.enum(['required', 'recommended', 'optional']),
  hasEnvironment: z.boolean(),
  environment: z.enum(['sandbox', 'production']).nullable(),
  runbook: z.string().nullable(),
  enabled: z.boolean(),
  settings: z.record(z.string(), SettingValueSchema),
  settingFields: z.array(SettingFieldSchema),
  secrets: z.array(SecretStatusSchema),
  updatedAtMs: z.number().nullable(),
});
export type IntegrationItem = z.infer<typeof IntegrationItemSchema>;

const ListOutputSchema = z.object({
  platformBrandId: z.string(),
  items: z.array(IntegrationItemSchema),
});
export type ListIntegrationsOutput = z.infer<typeof ListOutputSchema>;

const listFn: HttpsCallable<{ platformBrandId: string }, unknown> = httpsCallable(
  functions,
  'listIntegrationConfigs',
);

export async function callListIntegrations(
  platformBrandId: string,
): Promise<ListIntegrationsOutput> {
  const res = await listFn({ platformBrandId });
  const out = ListOutputSchema.safeParse(res.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('listIntegrationConfigs.output', out.error.flatten());
  }
  return out.data;
}

const SetConfigInputSchema = z.object({
  platformBrandId: z.string().min(1),
  service: z.string().min(1),
  enabled: z.boolean(),
  environment: z.enum(['sandbox', 'production']).optional(),
  settings: z.record(z.string(), SettingValueSchema),
});
export type SetIntegrationConfigInput = z.infer<typeof SetConfigInputSchema>;

const setConfigFn: HttpsCallable<SetIntegrationConfigInput, { ok: boolean }> = httpsCallable(
  functions,
  'setIntegrationConfig',
);

export async function callSetIntegrationConfig(input: SetIntegrationConfigInput): Promise<void> {
  const parsed = SetConfigInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('setIntegrationConfig.input', parsed.error.flatten());
  }
  await setConfigFn(parsed.data);
}

const SetSecretInputSchema = z.object({
  platformBrandId: z.string().min(1),
  service: z.string().min(1),
  secretName: z.string().min(1),
  value: z.string().min(1).max(4096),
});
export type SetIntegrationSecretInput = z.infer<typeof SetSecretInputSchema>;

const setSecretFn: HttpsCallable<SetIntegrationSecretInput, { ok: boolean }> = httpsCallable(
  functions,
  'setIntegrationSecret',
);

export async function callSetIntegrationSecret(input: SetIntegrationSecretInput): Promise<void> {
  const parsed = SetSecretInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('setIntegrationSecret.input', parsed.error.flatten());
  }
  await setSecretFn(parsed.data);
}
